import { HttpClient, HttpParams } from '@angular/common/http';
import { catchError, map, Observable, of } from 'rxjs';
import { environment } from '../../../environments/environment';
import {
  IPratica,
  PraticaSearchDTO,
  PraticaType,
  StatoPratica,
} from '../../../interfaces/pratica';
import {
  BePraticaAggregator,
  PraticaAggregator,
} from '../../settings/header-requests/pratica-aggregator';
import { PraticaChannelConfigurationDialogReturnData } from '../../settings/header-requests/pratica-channel-configuration';
import { typedEntries } from '../../_utils/typed-entries';
import { genHttpParams } from '../../_utils/utils';
import { ProductType } from '../../requests/models/product';
import { PraticaNotes } from '../../_widgets/notebook/model/pratica-notes';
import { BEAccount } from '../../../interfaces/account';
import { AuthService } from './auth.service';

export class PraticaService {
  private readonly queryEndpoint = `${environment.api}/pratica-query/pratica`;
  private readonly commandEndpoint = `${environment.api}/pratica-command/pratica`;
  private readonly aggregatorEndpoint = `${environment.api}/aggregatore-pratica/praticaAggregator`;
  private readonly processEndpoint = `${environment.api}/camunda/api/v1/process/nuova-richiesta/start`;
  private readonly richiestaDocumentaleEndpoint = `${environment.api}/camunda/api/v1/process/richiesta-documentale/start`;
  private readonly attestazioneBancariaEndPoint = `${environment.api}/camunda/api/v1/process/attestazione-bancaria`;
  private readonly istruttoriaEndpoint = `${environment.api}/camunda/api/v1/process/istruttoria`;
  private readonly notesQueryEndpoint = `${environment.api}/pratica-query/note-utente/pratica`;
  private readonly notesCommandEndpoint = `${environment.api}/pratica-command/note-utente`;

  constructor(private http: HttpClient, protected auth: AuthService) {}

  edit(id: string, updatedData: Partial<IPratica>) {
    return this.http.patch(`${this.commandEndpoint}/${id}`, updatedData);
  }

  getById(id: string) {
    return this.http.get<IPratica>(`${this.aggregatorEndpoint}/pratica/${id}`);
  }

  getPraticaById(id: string) {
    return this.getById(id);
  }

  search(
    page: number,
    size?: number,
    praticaFilter?: Partial<PraticaSearchDTO>,
    currentUser?: BEAccount
  ): Observable<{ items: PraticaAggregator[]; total: number }> {
    let endpoint = praticaFilter?.inbox ? 'search-inbox' : 'search';
    delete praticaFilter?.inbox;

    let params = genHttpParams(praticaFilter);

    params = params.set('page', page);
    if (size) params = params.set('size', size);

    return this.http
      .get<{ datas: BePraticaAggregator[]; total: number }>(
        `${this.aggregatorEndpoint}/${endpoint}`,
        { params }
      )
      .pipe(
        map((res) => {
          return {
            total: res.total,
            items: res.datas.map((el) => {
              return new PraticaAggregator(el, currentUser);
            }),
          };
        }),
        catchError((err) => {
          console.error(err);
          return of({ items: [], total: 0 });
        })
      );
  }

  create(formData: {
    target: string;
    identifier: string;
    subscriber: string;
    tipoProdotto: ProductType | null;
    tipoPratica: string;
  }) {
    return this.http.post<{ process: string }>(this.processEndpoint, formData);
  }

  assignChannel(
    body: PraticaChannelConfigurationDialogReturnData<'assign-channel'>['data']['channelInfo'] & {
      subscriber: string;
    },
    praticaId: string,
    productType: ProductType
  ) {
    let endpoint = this.istruttoriaEndpoint;
    if (productType === 'FINANZIAMENTO CRESCIAGRI') {
      endpoint += '-cresciagri';
    } else if (productType === 'FINANZIAMENTO CRESCIFARMA') {
      endpoint += '-farma';
    }
    return this.http.post(`${endpoint}/start`, {
      ...body,
      pratica: praticaId,
    });
  }

  attestazioneBancaria(body: { pratica: string; subscriber: string }) {
    return this.http.post(`${this.attestazioneBancariaEndPoint}/start`, {
      ...body,
    });
  }

  assignPratiche(userId: string, praticaId: string) {
    /*  let requests$ = praticheId.map((el) =>
      this.http.put(`${this.commandEndpoint}/${el}/${userId}`, {})
    );
    return forkJoin(requests$); */
    return this.http.put(`${this.commandEndpoint}/${praticaId}/${userId}`, {});
  }

  getCounters(praticaTypes: PraticaType, statiPratica: StatoPratica[] = []) {
    const params = new HttpParams()
      .set('statusList', statiPratica.toString())
      .set('praticaTypes', praticaTypes);
    return this.http
      .get<Record<StatoPratica, number>>(
        `${this.queryEndpoint}/count-pratiche-by-status`,
        { params }
      )
      .pipe(
        map((res) =>
          typedEntries(res).map(([key, value]) => ({
            state: key,
            counter: value,
          }))
        )
      );
  }

  getNotes(praticaId: string, userId: string) {
    return this.http.get<PraticaNotes | undefined>(
      `${this.notesQueryEndpoint}/${praticaId}/user/${userId}`
    );
  }

  updateNotes(
    praticaId: string,
    userId: string,
    note: string,
    notesId?: string
  ): Observable<{ id: string } | undefined> {
    let body: {
      note: string;
      pratica: string;
      user: string;
    } = {
      note,
      pratica: praticaId,
      user: userId,
    };
    return !notesId
      ? this.http.post<{ id: string }>(`${this.notesCommandEndpoint}`, body)
      : this.http.patch<undefined>(
          `${this.notesCommandEndpoint}/${notesId}`,
          body
        );
  }

  startRichiestaDocumentale(body: {
    pratica: string;
    commento: string;
    subscriber: string;
  }) {
    return this.http.post(`${this.richiestaDocumentaleEndpoint}`, body);
  }
}
